import React, { useEffect, useState } from 'react';
import { CheckIcon, ClipboardListIcon, FileSignatureIcon, FileTextIcon, LandmarkIcon, ListIcon, MailIcon, ReceiptTextIcon, ScrollTextIcon, ShareIcon, UsersIcon } from 'lucide-react';
import { useAssessment, useInstrumentConfig } from '../../contexts/AssessmentContext';
import { DocumentKind } from '../../types/assessment';
const icons: Record<DocumentKind, typeof FileTextIcon> = {
  'Bank Statement': LandmarkIcon,
  Invoice: ReceiptTextIcon,
  Schedule: ListIcon,
  Memo: FileTextIcon,
  Agreement: FileSignatureIcon,
  Letter: MailIcon,
  Register: ScrollTextIcon,
  'Transfer Form': FileSignatureIcon,
  Instruction: MailIcon,
  Minutes: UsersIcon,
  Statement: LandmarkIcon,
  Checklist: ClipboardListIcon
};
export function DocumentViewer() {
  const {
    openDocument,
    shareDocument,
    sharedDocs,
    locked,
    timeUp
  } = useAssessment();
  const config = useInstrumentConfig();
  const documents = config.documents;
  const [activeId, setActiveId] = useState(documents[0].id);

  // Instrument switches bring a different bundle with them.
  useEffect(() => {
    if (!documents.some(d => d.id === activeId)) setActiveId(documents[0].id);
  }, [documents, activeId]);
  const active = documents.find(d => d.id === activeId) || documents[0];
  const shared = sharedDocs.includes(active.id);
  const readOnly = locked || timeUp;
  useEffect(() => {
    openDocument(active.id, active.title);
  }, [active.id, active.title, openDocument]);
  return <div className="flex h-full min-h-0" data-id="element-226" data-mp="34uam5hbz6hxw">
      <aside className="w-64 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50" data-id="element-227" data-mp="34t8zefllk70c">
        <p className="px-4 pb-2 pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-228" data-mp="2zw93uymdimnh">
          Source documents
        </p>
        <ul className="pb-4" data-id="element-229" data-mp="y4dtcmbuso6u">
          {documents.map(doc => {
          const Icon = icons[doc.kind] || FileTextIcon;
          const selected = doc.id === activeId;
          return <li key={doc.id} data-id="element-230" data-mp="2nlhuajjo4rei">
                <button type="button" onClick={() => setActiveId(doc.id)} className={`flex w-full items-start gap-2.5 border-l-2 px-4 py-2.5 text-left transition-colors ${selected ? 'border-slate-900 bg-white' : 'border-transparent hover:bg-slate-100'}`} data-id="element-231" data-mp="2nl84avymik8t">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-232" data-mp="36bzgs6dwqqrf" />
                  <span className="min-w-0" data-id="element-233" data-mp="3bak7nf79ry37">
                    <span className={`block text-[13px] font-medium leading-snug ${selected ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-234" data-mp="2xbb3jdwc8ufl">
                      {doc.title}
                    </span>
                    <span className="font-mono-num text-[11px] text-slate-400" data-id="element-235" data-mp="1lyidc7j74e1n">{doc.id}</span>
                    {config.assistant.shareable && sharedDocs.includes(doc.id) && <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-800" data-id="element-236" data-mp="1icdm0f1la9xb">
                        <CheckIcon className="h-2.5 w-2.5" data-id="element-237" data-mp="1sb352b5820f5" />
                        shared
                      </span>}
                  </span>
                </button>
              </li>;
        })}
        </ul>
      </aside>

      <div className="min-w-0 flex-1 overflow-y-auto bg-slate-200/60 p-6" data-id="element-238" data-mp="uovzoz13g41q">
        <article className="mx-auto max-w-3xl rounded-sm bg-white shadow-lg shadow-slate-400/20 ring-1 ring-slate-300" data-id="element-239" data-mp="umjbi44qsxg8">
          <header className="border-b border-slate-200 px-8 py-6" data-id="element-240" data-mp="14n3epcbtoc13">
            <div className="flex items-start justify-between gap-4" data-id="element-241" data-mp="14lig9bme08xy">
              <div className="min-w-0" data-id="element-242" data-mp="gvxzmzdphnru">
                <span className="inline-flex items-center rounded-full bg-slate-900 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white" data-id="element-243" data-mp="guwajqzuv0r4">
                  {active.kind}
                </span>
                <h3 className="mt-3 text-lg font-semibold text-slate-900" data-id="element-244" data-mp="3fdqn6lczkhso">{active.title}</h3>
                <p className="mt-1 text-xs text-slate-500" data-id="element-245" data-mp="3kctqo1dbifyj">{active.issuer}</p>
              </div>

              {config.assistant.shareable && <button type="button" disabled={shared || readOnly} onClick={() => shareDocument(active.id, active.title)} className={`inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${shared ? 'cursor-default border-teal-200 bg-teal-50 text-teal-800' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:text-slate-900 disabled:opacity-40'}`} data-id="element-246" data-mp="3p4bsssr2uml3">
                  {shared ? <CheckIcon className="h-3.5 w-3.5" data-id="element-247" data-mp="2dowvfnuyhspg" /> : <ShareIcon className="h-3.5 w-3.5" data-id="element-248" data-mp="264vowfd7fo80" />}
                  {shared ? 'Shared with assistant' : 'Share with assistant'}
                </button>}
            </div>
          </header>
          <pre className="whitespace-pre-wrap px-8 py-6 font-mono-num text-[12.5px] leading-relaxed text-slate-800" data-id="element-249" data-mp="1wesz0o1dqqdw">
            {active.body.join('\n')}
          </pre>
          <footer className="border-t border-slate-200 px-8 py-3 font-mono-num text-[11px] text-slate-400" data-id="element-250" data-mp="1rdwlrjv51rvt">
            {active.id} · rendered from source · page 1 of 1
          </footer>
        </article>
      </div>
    </div>;
}